import { useRef, useState } from "react";
import { Paperclip, X } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { receiptFileError } from "../../lib/receiptFiles";
import { toast } from "../../stores/toast";
import { Button } from "../ui";

const MAX_RECEIPT_FILES = 3;

export function BankInReceiptUploadForm({
  orderId,
  orderTotal,
  hasRejectedReceipt,
  rejectReason,
  onUploaded,
}: {
  orderId: string;
  orderTotal: number;
  hasRejectedReceipt?: boolean;
  rejectReason?: string | null;
  onUploaded: () => void | Promise<void>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [receiptFiles, setReceiptFiles] = useState<File[]>([]);
  const [reference, setReference] = useState("");
  const [uploading, setUploading] = useState(false);

  function addReceiptFiles(files: FileList | null) {
    if (!files?.length) return;
    const incoming = Array.from(files);
    for (const file of incoming) {
      const err = receiptFileError(file);
      if (err) {
        toast("error", err);
        return;
      }
    }
    setReceiptFiles((prev) => {
      const merged = [...prev, ...incoming];
      if (merged.length > MAX_RECEIPT_FILES) {
        toast("error", `You can upload up to ${MAX_RECEIPT_FILES} receipts`);
      }
      return merged.slice(0, MAX_RECEIPT_FILES);
    });
  }

  function removeReceiptFile(index: number) {
    setReceiptFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function uploadReceipt() {
    if (receiptFiles.length === 0) {
      toast("error", "Choose at least one receipt file");
      return;
    }
    if (reference.trim().length < 2) {
      toast("error", "Enter the transfer reference");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of receiptFiles) {
        formData.append("files", file);
      }
      formData.append("orderId", orderId);
      formData.append("reference", reference.trim());
      formData.append("amount", String(orderTotal));
      await api("/payments/receipts", { method: "POST", formData });
      toast(
        "success",
        receiptFiles.length === 1
          ? "Receipt uploaded — we'll verify it shortly"
          : `${receiptFiles.length} receipts uploaded — we'll verify them shortly`,
      );
      setReceiptFiles([]);
      setReference("");
      await onUploaded();
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      {hasRejectedReceipt && (
        <p className="rounded-xl bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
          Previous receipt rejected{rejectReason ? `: ${rejectReason}` : ""}. Please upload again.
        </p>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          addReceiptFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={receiptFiles.length >= MAX_RECEIPT_FILES}
        className="w-full rounded-xl border border-dashed border-white/20 px-3 py-3 text-xs text-white/60 active:border-brand-pink/60 disabled:opacity-40"
      >
        {receiptFiles.length > 0 ? (
          <span className="inline-flex items-center justify-center gap-1.5">
            <Paperclip size={14} /> {receiptFiles.length} file{receiptFiles.length === 1 ? "" : "s"} selected
          </span>
        ) : (
          `Tap to attach receipts (JPEG, PNG, PDF — up to ${MAX_RECEIPT_FILES} files, 12 MB each)`
        )}
      </button>
      {receiptFiles.length > 0 && (
        <ul className="space-y-1.5 rounded-xl border border-white/10 bg-white/[0.02] p-2">
          {receiptFiles.map((file, index) => (
            <li key={`${file.name}-${index}`} className="flex items-center gap-2 text-[11px] text-white/70">
              <Paperclip size={12} className="shrink-0 text-white/40" />
              <span className="min-w-0 flex-1 truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => removeReceiptFile(index)}
                className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white"
                aria-label={`Remove ${file.name}`}
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        placeholder="Transfer reference / last 4 digits"
        value={reference}
        onChange={(e) => setReference(e.target.value)}
        className="w-full rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-base text-white placeholder-white/30 outline-none transition-colors focus:border-brand-pink/60 lg:text-sm"
      />
      <Button className="w-full" disabled={uploading || receiptFiles.length === 0} onClick={() => void uploadReceipt()}>
        {uploading ? "Uploading…" : receiptFiles.length > 1 ? `Submit ${receiptFiles.length} receipts` : "Submit receipt"}
      </Button>
    </>
  );
}
